/** @format */
import React from 'react';
import dot from 'dot-object';

/**
 * Parses entity errors from a response error object.
 *
 * @param responseError
 * @param errorPath
 * @param displayEntityErrors
 * @param defaultErrorMessage
 * @returns {string|false}
 */
export function parseEntityErrorMessage(
    responseError,
    errorPath,
    displayEntityErrors = true,
    defaultErrorMessage = 'There was an error.'
) {
    let errorMessage = '';
    let responseErrorMessage = responseError;

    if (!responseErrorMessage) {
        return false;
    }

    if (Array.isArray(responseErrorMessage)) {
        return responseErrorMessage[0];
    }

    try {
        let responseBodyParsed = responseErrorMessage;

        if (responseBodyParsed && typeof responseBodyParsed.message != 'undefined') {
            try {
                responseBodyParsed = JSON.parse(responseBodyParsed.message);
            } catch (e) {
                if (!getEntityMessage(responseBodyParsed, errorPath)) {
                    responseBodyParsed = responseBodyParsed.message;
                }
            }
        } else if (typeof responseBodyParsed === 'string') {
            responseBodyParsed = JSON.parse(responseBodyParsed);
        }
        if (displayEntityErrors && errorPath) {
            errorMessage = getEntityMessage(responseBodyParsed, errorPath);
        } else if (displayEntityErrors && typeof responseBodyParsed != 'string') {
            errorMessage = Object.values(dot.dot(responseBodyParsed)).join(' AND ');
        } else {
            if (typeof responseBodyParsed === 'string') {
                errorMessage = responseBodyParsed;
            } else if (responseBodyParsed && typeof responseBodyParsed.message != 'undefined') {
                errorMessage = responseBodyParsed.message;
            } else {
                errorMessage = defaultErrorMessage;
            }
        }
    } catch (e) {
        if (errorPath) {
            // We are looking for a specific error for a specific field
            // We did not find one, so not worries
            return false;
        }

        if (!responseErrorMessage) {
            errorMessage = defaultErrorMessage;
        } else {
            errorMessage = responseErrorMessage;
        }
    }

    return errorMessage;
}

/**
 * Retrieves the entity message from an error object using dot notation.
 *
 * @param errorMessage
 * @param errorPath
 * @returns {string|false}
 */
export function getEntityMessage(errorMessage, errorPath) {
    if (errorMessage && errorPath) {
        let errors = dot.pick(errorPath, errorMessage);
        if (errors) {
            errors = Object.values(errors);
            if (errors.length > 0) {
                return errors.join(' AND ');
            }
        }
    }
    return false;
}

/**
 * Determines if there is an error based on the provided error message and error path.
 *
 * @param errorMessage
 * @param errorPath
 * @param displayEntityErrors
 * @returns {boolean}
 */
export function hasError(errorMessage, errorPath, displayEntityErrors = true) {
    if (!displayEntityErrors) {
        if (getEntityMessage(errorMessage, errorPath) !== '') {
            return true;
        }
    }
    return false;
}

/**
 * Higher-order component that adds error handling to a component.
 *
 * @param ComponentContainer
 * @returns {React.Component}
 */
export default function withErrorHandling(ComponentContainer) {
    return class HandleError extends React.Component {
        static defaultProps = {
            defaultErrorMessage: 'There was an error.',
            displayEntityErrors: true,
            hasError: false,
            errorMessage: '',
            errorPath: false
        };

        constructor(props) {
            super(props);
        }

        render() {
            const {...props} = this.props;
            delete props['defaultErrorMessage'];
            delete props['displayEntityErrors'];
            let errorMessage = parseEntityErrorMessage(
                props.error,
                props.errorPath,
                this.props.displayEntityErrors,
                this.props.defaultErrorMessage
            );
            if (errorMessage) {
                props.hasError = true;
                props.errorMessage = errorMessage;
            }
            return <ComponentContainer {...props} />;
        }
    };
}
